import { HttpClient } from '@angular/common/http';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, finalize } from 'rxjs';
import { environment } from 'projects/cms/src/environments/environment';
import { User } from '../interface/user.interface';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  toastService = inject(ToastService);
  private http = inject(HttpClient);
  private router = inject(Router);
  private messageListener?: (event: MessageEvent) => void;

  public userSubject: BehaviorSubject<User | null> =
    new BehaviorSubject<User | null>(null);

  public tokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<
    string | null
  >(null);

  constructor() {
    const user = localStorage.getItem('adminUser');
    if (user) {
      try {
        this.userSubject.next(JSON.parse(user));
      } catch {
        this.userSubject.next(null);
      }
    }
    const token = localStorage.getItem('adminToken');
    if (token) {
      this.tokenSubject.next(token);
    }
  }

  public get userValue(): User | null {
    return this.userSubject.value;
  }

  public set setUserValue(user: User) {
    localStorage.setItem('adminUser', JSON.stringify(user));
    this.userSubject.next(user);
  }

  public get tokenValue(): string | null {
    return this.tokenSubject.value;
  }

  public set setTokenValue(token: string) {
    localStorage.setItem('adminToken', token);
    this.tokenSubject.next(token);
  }

  login(data: { email: string; password: string }) {
    this.http.post(`${environment.apiUrl}/login`, data).subscribe({
      next: (res: any) => {
        this.setUserValue = res.data;
        this.setTokenValue = res.token;
        this.router.navigate(['/']);
      },
      error: (err) => {
        this.toastService.addError({
          detail: err.message,
        });
      },
    });
  }

  logout() {
    this.http
      .post(`${environment.apiUrl}/logout`, '')
      .pipe(
        finalize(() => {
          this.removeUserData();
          this.router.navigate(['/auth/login']);
        }),
      )
      .subscribe();
  }

  forgotPassword(email: string) {
    this.http.post(`${environment.apiUrl}/forgot-password`, email).subscribe({
      next: (res: any) => {
        this.toastService.addSuccess({
          detail: res.message,
        });
      },
      error: (err) => {
        this.toastService.addError({
          detail: err.message,
        });
      },
    });
  }

  resetPassword(data: {
    email: string;
    password: string;
    repeat_password: string;
    token: string;
  }) {
    this.http.post(`${environment.apiUrl}/reset-password`, data).subscribe({
      next: (res: any) => {
        this.toastService.addSuccess({
          detail: res.message,
        });
        this.router.navigate(['/auth/login']);
      },
      error: (err) => {
        this.toastService.addError({
          detail: err.message,
        });
      },
    });
  }

  removeUserData() {
    localStorage.removeItem('adminUser');
    this.userSubject.next(null);
  }

  ngOnDestroy() {
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
    }
  }
}
